import { EventEmitter, Injectable, Injector } from '@angular/core';
import { LoggingService } from '../../shared/logging.service';
import { environment } from '../../../environments/environment';
import { MessageService } from 'primeng/api';
import * as rg4js from 'raygun4js';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorLogService } from '../../errors/error-log.service';
import { TimeoutError } from 'rxjs';

// Errors thrown by browser extensions (e.g. VPN extensions hooking XMLHttpRequest)
// are outside our control. We should avoid sending them over to Raygun
// Added also other browser support.
export const BROWSER_EXTENSION_PROTOCOLS: string[] = [
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'safari-web-extension://',
  'ms-browser-extension://',
  'webkit-masked-url://'
];

// Truthy when an extension protocol appears anywhere in the source's stack text.
// Works for an Error (its stack), a plain string, or a Raygun crash payload
// The payload's frame FileNames and Message surface through JSON.stringify (an Error
// stringifies to '{}', which is why its stack is appended explicitly).
export function isBrowserExtensionError(source: any): boolean {
  if (!source) {
    return false;
  }

  let haystack = typeof source === 'string' ? source : '';

  try {
    if (source.stack) {
      haystack = `${haystack} ${source.stack}`;
    }

    haystack = `${haystack} ${JSON.stringify(source)}`;
  } catch (inspectionError) {
    // Unserializable source (e.g. circular): match on whatever was collected.
  }

  return BROWSER_EXTENSION_PROTOCOLS.some((protocol: string): boolean => haystack.indexOf(protocol) > -1);
}

if (environment.production) {
  rg4js('apiKey', environment.raygunKey);
  rg4js('setVersion', environment.version);
  // Drop crash payloads originating from browser extensions before they are sent
  // (raygun4js aborts the send when the callback returns false). Covers raygun4js's
  // own window.onerror/unhandledrejection capture, which bypasses Angular's
  // ErrorHandler entirely.
  rg4js('onBeforeSend', (payload: any): any => (isBrowserExtensionError(payload) ? false : payload));
  rg4js('enableCrashReporting', true);
  rg4js('enablePulse', true);
}

export function sendToRaygun(error: any, customData?: any): void {
  // Raygun only on production environments
  if (!environment.production) {
    return;
  }

  rg4js('send', {
    error: error,
    customData: customData
  });
}

@Injectable()
export class GlobalErrorHandlerService {

  public errorEmitter: EventEmitter<boolean>;

  public isAppInErrorState = false;

  public chunkErrorStorageId = 'chunk_error_try';

  public chunkErrorTimeStorageId = 'chunk_error_try_time';

  constructor(private injector: Injector) {
    this.errorEmitter = new EventEmitter();
  }

  public handleError(error: Error): void {
    let timeout = 0;
    const loggingService = this.injector.get(LoggingService);
    const errorLogService = this.injector.get(ErrorLogService);
    const growlService = this.injector.get(MessageService);
    const message = error.message ? error.message : error.toString();

    // Wrap in try catch to make sure we don't cause error on the error handler
    try {
      errorLogService.setError(`${error.name} - ${error.message}`);
    } catch (e) {
      errorLogService.setError('An unknown error occurred.');
    }

    if (isBrowserExtensionError(error)) {
      // Errors originating from browser extensions (e.g. Urban VPN hooking XHR)
      // are outside our control: do not send to Raygun.
      return;
    }

    if (this.isThirdPartyScriptError(error)) {
      // Errors thrown from third-party scripts (e.g. Microsoft Clarity) should
      // not tear down the app. Report them to Raygun and continue.
      this.sendToRaygun(error);
      return;
    }

    if (error instanceof HttpErrorResponse ||
      this.errorStringify(error)
        .indexOf('HttpErrorResponse') > -1) {
      // HTTP-based errors has it's own handler.
      // They must be handled in HttpRequestErrorInterceptor.
      this.sendToRaygun(error);
      return;
    }

    if (error instanceof TimeoutError) {
      // Timeout errors are thrown in rxjs observables when we put a timeout limit explicitly
      // It's not really fatal so we don't need to redirect to 404 pages;
      // This notification will only show if the error has not been handled in subscribe();
      growlService.add({
        severity: 'error', summary: 'Error',
        detail: 'System Timeout'
      });

      this.sendToRaygun(error);
      return;
    }

    if (typeof error === 'string' && error === 'Timeout') {
      // Similar to above, this one is a timeout we get from ReCaptcha when
      // it can't find the instance of the button anymore.
      // No need to growl any errors as this is third party
      return;
    }

    if (loggingService && environment.production) {
      loggingService.error(message);
    }

    if (error.name === 'ChunkLoadError') {
      this.chunkErrorClearRetry();

      // Can't do this infinitely otherwise the user will be on an
      // infinite reload loop if for example, they lose connection.
      if (this.chunkErrorGetRetryCount() > 2) {
        this.sendToRaygun(error);
        // Check if we can get the router instance.
        const router = this.injector.get(Router);
        if (router) {
          router.navigate(['/error-404'], { skipLocationChange: true });
        }

        return;
      }

      setTimeout(() => {
        this.chunkErrorRetryAdd();
        this.chunkErrorSetRetryTime();

        window.location.reload();
        // 2 second before trying so we can give the user time to breath
        // otherwise it will look like an infinite loop error
      }, 2000);

      return;
    }

    if (message.indexOf('The connection has not been established yet') > 0) {
      /* empty */
    } else if (message.indexOf('ExpressionChangedAfterItHasBeenCheckedError') < 0) {
      if (!environment.production) {
        timeout = 600000;
        growlService.add({
          severity: 'error', summary: 'Error',
          detail: 'An exception occurred, please capture this exception and refresh the page.'
        });
      } else {
        this.sendToRaygun(error);
      }
      if (!this.isAppInErrorState) {
        this.isAppInErrorState = true;
        this.errorEmitter.emit(this.isAppInErrorState);
      }
      if (loggingService) {
        loggingService.info('Redirecting to error page');
      }
      setTimeout(() => {
        // Uncaught promises are not fatal errors, no need to redirect them to 404.
        // This will conflict with the rest of our online/offline handling and redirection
        if (message.indexOf('Uncaught (in promise)') > -1) {
          return;
        }

        const router = this.injector.get(Router);

        // Check if we can get the router instance.
        if (router) {
          router.navigate(['/error-404'], { skipLocationChange: true });
        } else {
          // If we don't have the router instance, hard-navigate to refresh the app and display the error page.
          window.location.href = '/error-404';
        }

      }, timeout);
    }
  }

  private sendToRaygun(error: any, customData?: any): void {
    // Raygun only on production environments
    if (!environment.production) {
      return;
    }

    sendToRaygun(error, customData);
  }

  /**
   * Stringifies the error.
   * Used if the instanceOf does not work as expected
   *
   * @param error
   */
  private errorStringify(error: Error): string {
    // Needs to wrapped in try-catch to handle unexpected behavior in all types of error
    try {
      return error.toString();
    } catch (e) {
      return '';
    }
  }

  // Microsoft Clarity (clarity.ms) has crashed internally in prod with
  // "Cannot read properties of null (reading 'sequence')"; without this guard,
  // those errors fall through to the generic handler and redirect users to /error-404.
  private isThirdPartyScriptError(error: Error): boolean {
    if (!error) {
      return false;
    }

    const stack = (error as Error & { stack?: string }).stack;
    const message = error.message ? error.message : '';
    const haystack = `${stack ? stack : ''} ${message}`;

    const thirdPartyOrigins = [
      'clarity.ms',
      'scripts.clarity.ms'
    ];

    return thirdPartyOrigins.some((origin: string): boolean => haystack.indexOf(origin) > -1);
  }

  private chunkErrorRetryAdd(): void {
    let count = +window.localStorage.getItem(this.chunkErrorStorageId);
    count++;

    window.localStorage.setItem(this.chunkErrorStorageId, count.toString());
  }

  private chunkErrorGetRetryCount(): number {
    const count = +window.localStorage.getItem(this.chunkErrorStorageId);
    if (count) {
      return count;
    }

    return 0;
  }

  private chunkErrorSetRetryTime(): void {
    const now = Date.now();
    window.localStorage.setItem(this.chunkErrorTimeStorageId, now.toString());
  }

  private chunkErrorClearRetry(): void {
    const retryClearance = 300000; // 5 minutes
    // const retryClearance = 30000; // 30 seconds for debugging only
    const time = +window.localStorage.getItem(this.chunkErrorTimeStorageId);

    if (Date.now() > (time + retryClearance)) {
      window.localStorage.removeItem(this.chunkErrorTimeStorageId);
      window.localStorage.removeItem(this.chunkErrorStorageId);
    } else {
      // console.log('Nothing to clear yet for chunkRetry');
    }
  }
}
